import { DocumentData, getDocs, limit, orderBy, query } from "firebase/firestore";
import { COLLECTIONS } from "./collectionNames";
import { adminCollection, dateField, logDevelopmentError, stringArrayField, stringField } from "./firestoreHelpers";
import { FounderBriefStatus, statusLabel } from "./founderBriefService";

export type FounderJournalPeriodFilter = "all" | "today" | "week" | "month" | "milestones";

export type FounderJournalSummary = {
  title: string;
  body: string;
};

export type FounderJournalMilestone = {
  id: string;
  title: string;
  detail: string;
};

export type FounderJournalCorrection = {
  id: string;
  note: string;
  addedAt: Date | null;
};

export type FounderJournalEntry = {
  id: string;
  dateKey: string;
  date: Date;
  founderPulse: {
    status: FounderBriefStatus;
    label: string;
    summary: string;
  };
  todaysStory: string[];
  communitySummary: FounderJournalSummary;
  businessSummary: FounderJournalSummary;
  productHealth: FounderJournalSummary;
  infrastructureStatus: FounderJournalSummary;
  reflection: string;
  milestones: FounderJournalMilestone[];
  searchTokens: string[];
  finalizedAt: Date | null;
  corrections: FounderJournalCorrection[];
  source: string;
};

export type FounderJournalArchive = {
  entries: FounderJournalEntry[];
  duplicateDateKeys: string[];
  lastUpdated: Date;
};

const JOURNAL_LIMIT = 365;
export const FOUNDER_JOURNAL_ENTRY_ID_POLICY = "YYYY-MM-DD";
export const FOUNDER_JOURNAL_IMMUTABILITY_POLICY =
  "Founder Journal entries are finalized once per calendar day, stored with a YYYY-MM-DD document identity, and corrected only by appended notes.";

function textValue(data: DocumentData, keys: string[], fallback: string) {
  for (const key of keys) {
    const value = stringField(data, key);
    if (value) return value;
  }
  return fallback;
}

function dateKeyFor(date: Date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0")
  ].join("-");
}

function dateFromKey(dateKey: string) {
  const [year, month, day] = dateKey.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function statusFrom(data: DocumentData): FounderBriefStatus {
  const raw = textValue(data, ["status", "pulseStatus", "founderPulseStatus"], "healthy").toLowerCase().replace(/_/g, "-");
  if (raw === "growing" || raw === "healthy" || raw === "watch" || raw === "action-needed") return raw;
  if (raw === "warning" || raw === "watching") return "watch";
  if (raw === "critical" || raw === "actionneeded") return "action-needed";
  return "healthy";
}

function summaryFrom(data: DocumentData, keys: string[], fallbackTitle: string, fallbackBody: string): FounderJournalSummary {
  for (const key of keys) {
    const value = data[key];
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const objectValue = value as DocumentData;
      return {
        title: textValue(objectValue, ["title", "headline", "label"], fallbackTitle),
        body: textValue(objectValue, ["body", "summary", "detail", "sentence"], fallbackBody)
      };
    }
    if (typeof value === "string" && value.trim().length > 0) {
      return {
        title: fallbackTitle,
        body: value
      };
    }
  }

  return {
    title: fallbackTitle,
    body: fallbackBody
  };
}

function stringList(data: DocumentData, keys: string[], fallback: string[] = []) {
  for (const key of keys) {
    const value = data[key];
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object" && !Array.isArray(item)) {
            return textValue(item as DocumentData, ["text", "title", "headline", "summary"], "");
          }
          return "";
        })
        .filter((item) => item.trim().length > 0)
        .slice(0, 5);
    }
  }
  return fallback;
}

function milestonesFrom(data: DocumentData): FounderJournalMilestone[] {
  const rawMilestones = data.majorMilestones ?? data.milestones ?? [];
  if (!Array.isArray(rawMilestones)) return [];

  return rawMilestones
    .filter((milestone): milestone is DocumentData => milestone && typeof milestone === "object" && !Array.isArray(milestone))
    .map((milestone, index) => ({
      id: stringField(milestone, "id") ?? `milestone-${index}`,
      title: textValue(milestone, ["title", "headline"], "Milestone recorded."),
      detail: textValue(milestone, ["detail", "body", "summary"], "A meaningful Chor milestone became part of the archive.")
    }));
}

function correctionsFrom(data: DocumentData): FounderJournalCorrection[] {
  const rawCorrections = data.corrections ?? data.appendedCorrections ?? [];
  if (!Array.isArray(rawCorrections)) return [];

  return rawCorrections
    .filter((correction): correction is DocumentData => correction && typeof correction === "object" && !Array.isArray(correction))
    .map((correction, index) => ({
      id: stringField(correction, "id") ?? `correction-${index}`,
      note: textValue(correction, ["note", "body", "detail"], "Correction note appended."),
      addedAt: dateField(correction, "addedAt") ?? dateField(correction, "createdAt")
    }));
}

function reflectionText(data: DocumentData) {
  const reflection = data.founderReflection ?? data.reflection;
  if (reflection && typeof reflection === "object" && !Array.isArray(reflection)) {
    return textValue(reflection as DocumentData, ["text", "body", "reflection"], "Today was quiet. Tomorrow brings another opportunity to help dancers grow.");
  }
  return textValue(data, ["founderReflection", "reflection"], "Today was quiet. Tomorrow brings another opportunity to help dancers grow.");
}

function entrySearchTokens(data: DocumentData, entry: FounderJournalEntry) {
  const explicitTokens = stringArrayField(data, "searchTokens");
  const textTokens = [
    entry.dateKey,
    entry.founderPulse.summary,
    ...entry.todaysStory,
    entry.communitySummary.title,
    entry.communitySummary.body,
    entry.businessSummary.title,
    entry.businessSummary.body,
    entry.productHealth.title,
    entry.productHealth.body,
    entry.infrastructureStatus.title,
    entry.infrastructureStatus.body,
    entry.reflection,
    ...entry.milestones.flatMap((milestone) => [milestone.title, milestone.detail])
  ];

  return [...explicitTokens, ...textTokens].map((token) => token.toLowerCase());
}

function entryFromDocument(id: string, data: DocumentData): FounderJournalEntry {
  const dateKey = textValue(data, ["dateKey", "journalDateKey", "dayKey"], id);
  const date = dateField(data, "date") ?? dateField(data, "journalDate") ?? dateField(data, "finalizedDate") ?? dateFromKey(dateKey) ?? new Date(0);
  const pulseStatus = statusFrom(data.founderPulse && typeof data.founderPulse === "object" ? (data.founderPulse as DocumentData) : data);
  const pulseData = data.founderPulse && typeof data.founderPulse === "object" ? (data.founderPulse as DocumentData) : data;
  const milestones = milestonesFrom(data);

  const entry: FounderJournalEntry = {
    id,
    dateKey,
    date,
    founderPulse: {
      status: pulseStatus,
      label: statusLabel(pulseStatus),
      summary: textValue(pulseData, ["summary", "sentence", "body"], "The daily pulse was archived.")
    },
    todaysStory: stringList(data, ["todaysStory", "dailyStory", "stories"], ["Today was quiet. Chor stayed ready for tomorrow."]),
    communitySummary: summaryFrom(data, ["communitySummary", "community"], "Community", "Community truth was archived for this day."),
    businessSummary: summaryFrom(data, ["businessSummary", "business"], "Business", "Business truth was archived for this day."),
    productHealth: summaryFrom(data, ["productHealth", "product"], "Product Health", "Product health truth was archived for this day."),
    infrastructureStatus: summaryFrom(data, ["infrastructureStatus", "infrastructure"], "Infrastructure", "Infrastructure truth was archived for this day."),
    reflection: reflectionText(data),
    milestones,
    searchTokens: [],
    finalizedAt: dateField(data, "finalizedAt"),
    corrections: correctionsFrom(data),
    source: COLLECTIONS.founderJournalEntries
  };

  return {
    ...entry,
    searchTokens: entrySearchTokens(data, entry)
  };
}

function duplicateDateKeys(entries: FounderJournalEntry[]) {
  const counts = new Map<string, number>();
  entries.forEach((entry) => counts.set(entry.dateKey, (counts.get(entry.dateKey) ?? 0) + 1));
  return Array.from(counts.entries())
    .filter(([, count]) => count > 1)
    .map(([dateKey]) => dateKey);
}

export function filterFounderJournalEntries(
  entries: FounderJournalEntry[],
  options: { period: FounderJournalPeriodFilter; searchTerm: string; jumpDate: string },
  now = new Date()
) {
  const normalizedSearch = options.searchTerm.trim().toLowerCase();
  const todayKey = dateKeyFor(now);
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - 7);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  return entries.filter((entry) => {
    if (options.jumpDate && entry.dateKey !== options.jumpDate) return false;
    if (options.period === "today" && entry.dateKey !== todayKey) return false;
    if (options.period === "week" && entry.date < weekStart) return false;
    if (options.period === "month" && entry.date < monthStart) return false;
    if (options.period === "milestones" && entry.milestones.length === 0) return false;
    if (normalizedSearch && !entry.searchTokens.some((token) => token.includes(normalizedSearch))) return false;
    return true;
  });
}

export async function fetchFounderJournalArchive(): Promise<FounderJournalArchive> {
  try {
    const snapshot = await getDocs(
      query(adminCollection(COLLECTIONS.founderJournalEntries), orderBy("date", "desc"), limit(JOURNAL_LIMIT))
    );
    const entries = snapshot.docs.map((document) => entryFromDocument(document.id, document.data()));

    return {
      entries: entries.sort((left, right) => right.date.getTime() - left.date.getTime()),
      duplicateDateKeys: duplicateDateKeys(entries),
      lastUpdated: new Date()
    };
  } catch (error) {
    logDevelopmentError(error);
    return {
      entries: [],
      duplicateDateKeys: [],
      lastUpdated: new Date()
    };
  }
}
